import { Position } from '../constants';

import { Entity, EntityData, EntityType } from './Entity';

interface WanderConfig {
  readonly wanderRadius: number;
  readonly minMoveInterval: number;
  readonly maxMoveInterval: number;
  readonly idleChance?: number;
}

interface DirectionOffset {
  readonly x: number;
  readonly y: number;
}

const CARDINAL_DIRECTIONS: DirectionOffset[] = [
  { x: 1, y: 0 },
  { x: -1, y: 0 },
  { x: 0, y: 1 },
  { x: 0, y: -1 },
];

const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

/**
 * Base class for small ambient wildlife that wanders around the world.
 */
export abstract class AnimalEntity extends Entity {
  protected readonly homePosition: Position;
  private readonly wanderRadius: number;
  private readonly minMoveInterval: number;
  private readonly maxMoveInterval: number;
  private readonly baseIdleChance: number;

  private elapsedSinceMoveMs: number = 0;
  private nextMoveDelayMs: number;

  constructor(
    id: string,
    type: EntityType,
    position: Position,
    config: WanderConfig
  ) {
    super(id, type, position);

    this.homePosition = { ...position };
    this.wanderRadius = Math.max(1, config.wanderRadius);
    this.minMoveInterval = Math.max(100, config.minMoveInterval);
    this.maxMoveInterval = Math.max(
      this.minMoveInterval,
      config.maxMoveInterval
    );
    this.baseIdleChance = clamp(config.idleChance ?? 0.25, 0, 1);
    this.nextMoveDelayMs = this.getRandomInterval();
  }

  update(deltaTime: number): void {
    this.elapsedSinceMoveMs += deltaTime;
    if (this.elapsedSinceMoveMs < this.nextMoveDelayMs) {
      this.onUpdateWhileWaiting(deltaTime);
      return;
    }

    this.elapsedSinceMoveMs = 0;
    this.nextMoveDelayMs = this.getRandomInterval();

    if (!this.shouldAttemptMove()) {
      this.onIdle();
      return;
    }

    if (Math.random() < this.getIdleChance()) {
      this.onIdle();
      return;
    }

    const nextPosition = this.chooseNextPosition();
    if (nextPosition) {
      this.moveTo(nextPosition);
      this.onMove(nextPosition);
    } else {
      this.onIdle();
    }
  }

  override isSolid(): boolean {
    return false;
  }

  override canPlace(): boolean {
    return false;
  }

  override serialize(): EntityData {
    return {
      id: this.id,
      type: this.type,
      position: this.position,
      homePosition: this.homePosition,
      wanderRadius: this.wanderRadius,
    };
  }

  override collide(_other: Entity): boolean {
    return false;
  }

  protected onIdle(): void {
    // Default no-op; subclasses can override for custom behaviour
  }

  protected onMove(_position: Position): void {
    // Default no-op; subclasses can override for custom behaviour
  }

  protected onUpdateWhileWaiting(_deltaTime: number): void {
    // Subclasses may override to update timers while waiting for the next move
  }

  protected shouldAttemptMove(): boolean {
    return true;
  }

  protected getIdleChance(): number {
    return this.baseIdleChance;
  }

  protected chooseNextPosition(): Position | null {
    const shuffledDirections = [...CARDINAL_DIRECTIONS];

    for (let i = shuffledDirections.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const first = shuffledDirections[i];
      const second = shuffledDirections[j];

      if (!first || !second) {
        continue;
      }

      shuffledDirections[i] = second;
      shuffledDirections[j] = first;
    }

    for (const direction of shuffledDirections) {
      const candidate = {
        x: this.position.x + direction.x,
        y: this.position.y + direction.y,
      };

      if (this.isWithinWanderRadius(candidate)) {
        return candidate;
      }
    }

    return null;
  }

  protected isWithinWanderRadius(position: Position): boolean {
    const dx = position.x - this.homePosition.x;
    const dy = position.y - this.homePosition.y;
    return dx * dx + dy * dy <= this.wanderRadius * this.wanderRadius;
  }

  protected triggerImmediateMove(): void {
    this.elapsedSinceMoveMs = this.nextMoveDelayMs;
  }

  private getRandomInterval(): number {
    const range = this.maxMoveInterval - this.minMoveInterval;
    return this.minMoveInterval + Math.random() * range;
  }
}

interface RabbitOptions {
  readonly startleOnSpawn?: boolean;
}

export class RabbitEntity extends AnimalEntity {
  private isAlert: boolean;
  private calmTimerMs: number = 0;
  private readonly calmDurationMs: number = 3500;

  constructor(id: string, position: Position, options: RabbitOptions = {}) {
    super(id, EntityType.RABBIT, position, {
      wanderRadius: 6,
      minMoveInterval: 700,
      maxMoveInterval: 1400,
      idleChance: 0.3,
    });

    this.isAlert = options.startleOnSpawn ?? false;
  }

  override getDisplayChar(): string {
    return '🐇';
  }

  override getCSSClass(): string {
    return `animal animal-rabbit${this.isAlert ? ' is-alert' : ''}`;
  }

  override getTooltip(): string {
    return this.isAlert ? 'Startled rabbit' : 'Rabbit';
  }

  override update(deltaTime: number): void {
    if (this.isAlert) {
      this.calmTimerMs += deltaTime;
      if (this.calmTimerMs >= this.calmDurationMs) {
        this.isAlert = false;
        this.calmTimerMs = 0;
      }
    }

    super.update(deltaTime);
  }

  override collide(other: Entity): boolean {
    if (other === this) {
      return false;
    }

    this.isAlert = true;
    this.calmTimerMs = 0;

    const dx = this.position.x - other.position.x;
    const dy = this.position.y - other.position.y;

    let fleeDirection: DirectionOffset;
    if (Math.abs(dx) > Math.abs(dy)) {
      fleeDirection = { x: Math.sign(dx) || 1, y: 0 };
    } else if (Math.abs(dy) > 0) {
      fleeDirection = { x: 0, y: Math.sign(dy) || 1 };
    } else {
      const randomDirection =
        CARDINAL_DIRECTIONS[
          Math.floor(Math.random() * CARDINAL_DIRECTIONS.length)
        ];
      fleeDirection = randomDirection ?? { x: 1, y: 0 };
    }

    const candidatePosition: Position = {
      x: this.position.x + fleeDirection.x,
      y: this.position.y + fleeDirection.y,
    };

    if (this.isWithinWanderRadius(candidatePosition)) {
      this.moveTo(candidatePosition);
    }

    this.triggerImmediateMove();
    return false;
  }

  protected override getIdleChance(): number {
    return this.isAlert ? 0.05 : super.getIdleChance();
  }

  protected override onMove(_position: Position): void {
    if (this.isAlert) {
      this.calmTimerMs = 0;
    }
  }

  override serialize(): EntityData {
    return {
      ...super.serialize(),
      isAlert: this.isAlert,
    };
  }
}

interface WormOptions {
  readonly startHidden?: boolean;
  readonly initialSurfaceTimerMs?: number;
}

export class WormEntity extends AnimalEntity {
  private isUnderground: boolean;
  private surfaceTimerMs: number;
  private readonly minSurfaceDurationMs: number = 1600;
  private readonly maxSurfaceDurationMs: number = 4200;

  constructor(id: string, position: Position, options: WormOptions = {}) {
    super(id, EntityType.WORM, position, {
      wanderRadius: 3,
      minMoveInterval: 1200,
      maxMoveInterval: 2400,
      idleChance: 0.55,
    });

    this.isUnderground = options.startHidden ?? Math.random() < 0.5;
    this.surfaceTimerMs =
      options.initialSurfaceTimerMs ?? this.getNextSurfaceInterval();
  }

  override getDisplayChar(): string {
    return this.isUnderground ? '⬤' : '🪱';
  }

  override getCSSClass(): string {
    return `animal animal-worm${this.isUnderground ? ' is-hidden' : ''}`;
  }

  override getTooltip(): string {
    return this.isUnderground ? 'Hidden worm' : 'Worm';
  }

  override update(deltaTime: number): void {
    this.surfaceTimerMs -= deltaTime;
    if (this.surfaceTimerMs <= 0) {
      this.isUnderground = !this.isUnderground;
      this.surfaceTimerMs = this.getNextSurfaceInterval();
    }

    super.update(deltaTime);
  }

  protected override shouldAttemptMove(): boolean {
    return !this.isUnderground;
  }

  protected override onIdle(): void {
    // Worms wiggle subtly even when idle by toggling their hidden state timer faster
    this.surfaceTimerMs -= 50;
  }

  override collide(_other: Entity): boolean {
    this.isUnderground = true;
    this.surfaceTimerMs = this.getNextSurfaceInterval();
    return false;
  }

  override serialize(): EntityData {
    return {
      ...super.serialize(),
      isUnderground: this.isUnderground,
      surfaceTimerMs: this.surfaceTimerMs,
    };
  }

  private getNextSurfaceInterval(): number {
    const range = this.maxSurfaceDurationMs - this.minSurfaceDurationMs;
    return this.minSurfaceDurationMs + Math.random() * range;
  }
}
